/**
 * Clinic portal consent list hook.
 * API calls go through @/services/main/consent; errors surfaced via toast.
 */

import { useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import { getConsentsList, type ConsentListQueryParams } from '@/services/main/consent';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { useClinicContext } from './useClinicContext';
import { useClinicPortalServiceType } from './clinicPortalServiceType';

export type ConsentListFilters = ConsentListQueryParams;

/**
 * Paginated consent list for a patient (tenant + clinic from auth context).
 */
export const useConsentList = (
  patientId?: string,
  filters?: ConsentListQueryParams,
  options?: { enabled?: boolean }
) => {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useClinicPortalServiceType();
  const toast = useAppToast();

  const { data, isLoading, isFetching, isError, isPending, refetch, error } = useQuery({
    queryKey: ['consents', 'list', tenant, clinicNumber, patientId, serviceType, filters],
    queryFn: () => getConsentsList(tenant! || '', clinicNumber! || '', patientId!, serviceType, filters),
    enabled: (options?.enabled ?? true) && !!tenant && !!clinicNumber && !!patientId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isFetching, isError, isPending, refetch, error };
};

export default useConsentList;
